import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ArrowRight, Check, X, RotateCcw, Download, Award } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useSelectedCountry } from "@/context/SelectedCountryContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = { INTRO: "intro", FORM: "form", QUIZ: "quiz", RESULT: "result" };

export default function Quiz() {
  const { t } = useLanguage();
  const { slug: selectedSlug, country: selectedCountry } = useSelectedCountry();
  const [step, setStep] = useState(STEPS.INTRO);
  const [form, setForm] = useState({ name: "", passport: "", email: "", country_slug: "" });
  const [formError, setFormError] = useState("");
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loadingQs, setLoadingQs] = useState(false);
  const certRef = useRef(null);

  // Keep form.country_slug in sync with the globally-selected destination.
  useEffect(() => {
    setForm((f) => ({ ...f, country_slug: selectedSlug || "" }));
  }, [selectedSlug]);

  const beginForm = () => { setFormError(""); setStep(STEPS.FORM); };

  const startQuiz = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!form.name.trim()) return setFormError("Please enter your full name.");
    if (!form.passport.trim()) return setFormError("Please enter your passport number.");
    if (!form.email.trim()) return setFormError("Please enter your email address.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return setFormError("Please enter a valid email address.");
    if (!form.country_slug) return setFormError("Please select your destination country.");
    setFormError("");
    setLoadingQs(true);
    try {
      const r = await axios.get(`${API}/quiz/questions`, {
        params: { country: form.country_slug, count: 10 },
      });
      setQuestions(r.data);
      setIdx(0);
      setAnswers({});
      setResult(null);
      setStep(STEPS.QUIZ);
    } catch (err) {
      setFormError("Couldn't load questions. Please try again.");
    } finally {
      setLoadingQs(false);
    }
  };

  const select = (qid, optionIdx) => setAnswers((a) => ({ ...a, [qid]: optionIdx }));

  const next = async () => {
    if (idx < questions.length - 1) {
      setIdx(idx + 1);
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/quiz/submit`, {
        name: form.name,
        passport: form.passport,
        email: form.email,
        country_slug: form.country_slug,
        answers: Object.entries(answers).map(([qid, sel]) => {
          const q = questions.find((x) => x.id === qid);
          return { question_id: qid, selected: sel, options: q ? q.options : undefined };
        }),
      });
      setResult(r.data);
      setStep(STEPS.RESULT);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const restart = () => {
    setStep(STEPS.INTRO);
    setForm({ name: "", passport: "", email: "", country_slug: selectedSlug || "" });
    setQuestions([]);
    setAnswers({});
    setResult(null);
    setIdx(0);
  };

  const printCertificate = () => {
    if (!certRef.current) return;
    const w = window.open("", "PRINT", "height=900,width=1200");
    w.document.write(`<html><head><title>Civic Sense Abroad — Certificate</title>
      <style>
        body{margin:0;font-family:Georgia,serif;background:#fff;}
        .cert{width:1000px;margin:20px auto;padding:60px;border:14px double #002FA7;position:relative;}
        .cert::after{content:'';position:absolute;inset:14px;border:2px solid #002FA7;pointer-events:none;}
        h1{font-size:48px;color:#002FA7;margin:0 0 8px;text-align:center;letter-spacing:2px;}
        .sub{text-align:center;color:#475569;letter-spacing:6px;text-transform:uppercase;font-size:12px;margin-bottom:30px;}
        .name{font-size:42px;text-align:center;color:#0F172A;margin:24px 0 8px;font-weight:bold;}
        .line{height:1px;background:#0F172A;width:60%;margin:0 auto 20px;}
        .body{text-align:center;color:#0F172A;font-size:18px;line-height:1.7;max-width:780px;margin:0 auto;}
        .meta{display:flex;justify-content:space-between;margin-top:60px;color:#475569;font-size:13px;}
        .seal{position:absolute;right:80px;bottom:80px;width:120px;height:120px;border-radius:50%;border:3px solid #002FA7;display:flex;align-items:center;justify-content:center;color:#002FA7;font-weight:bold;text-align:center;font-size:11px;letter-spacing:1px;}
      </style></head><body>${certRef.current.outerHTML}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); w.close(); }, 350);
  };

  // ---------- INTRO ----------
  if (step === STEPS.INTRO) {
    return (
      <div data-testid="quiz-intro" className="fade-in max-w-3xl mx-auto px-6 py-24 text-center">
        <div className="overline mb-3">10 Questions · Country-specific · 3 Minutes</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-[#0F172A]">
          How civic-aware are you when you fly?
        </h1>
        <p className="mt-5 text-[#475569] text-lg max-w-xl mx-auto leading-relaxed">
          Test yourself on country-specific etiquette and universal travel manners. Score 9 or 10 and
          earn a Certified Civic-Aware Traveller certificate.
        </p>
        <button data-testid="quiz-start-button" onClick={beginForm} className="btn-primary mt-8">
          {t.quiz.start} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>
    );
  }

  // ---------- FORM ----------
  if (step === STEPS.FORM) {
    return (
      <div data-testid="quiz-form" className="fade-in max-w-xl mx-auto px-6 py-16">
        <div className="overline mb-3">Tell us about your trip</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-[#0F172A]">
          Before we begin
        </h1>
        <p className="mt-3 text-[#475569] leading-relaxed">
          We use these details to personalise your quiz and your certificate (if you score above 8).
        </p>

        <form onSubmit={startQuiz} noValidate className="mt-10 space-y-5">
          <div>
            <label className="overline block mb-2">Full Name (as on passport)</label>
            <input
              data-testid="quiz-form-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full p-4 border border-[#E2E8F0] rounded-sm focus:border-[#002FA7] outline-none"
              placeholder="e.g. Aman Sharma"
            />
          </div>
          <div>
            <label className="overline block mb-2">Passport Number</label>
            <input
              data-testid="quiz-form-passport"
              value={form.passport}
              onChange={(e) => setForm({ ...form, passport: e.target.value.toUpperCase() })}
              className="w-full p-4 border border-[#E2E8F0] rounded-sm focus:border-[#002FA7] outline-none tracking-wider"
              placeholder="e.g. M1234567"
              maxLength={20}
            />
            <p className="text-xs text-[#94A3B8] mt-1">Used only on your certificate. Not stored beyond this session.</p>
          </div>
          <div>
            <label className="overline block mb-2">Email Address</label>
            <input
              data-testid="quiz-form-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full p-4 border border-[#E2E8F0] rounded-sm focus:border-[#002FA7] outline-none"
              placeholder="you@example.com"
              autoComplete="email"
            />
            <p className="text-xs text-[#94A3B8] mt-1">We'll send your certificate to this email if you score 9 or higher.</p>
          </div>
          <div>
            <label className="overline block mb-2">Destination Country</label>
            <div
              data-testid="quiz-form-country-chip"
              className="w-full p-4 border border-[#002FA7]/30 rounded-sm bg-[#EEF2FF] text-[#0F172A] flex items-center gap-2"
            >
              {selectedCountry ? (
                <>
                  <span className="text-lg">{selectedCountry.flag}</span>
                  <span className="font-medium">{selectedCountry.name}</span>
                  <span className="ml-auto text-xs text-[#475569]">change via header</span>
                </>
              ) : (
                <span className="text-[#475569] text-sm">Pick a destination from the header pill.</span>
              )}
            </div>
          </div>

          {formError && (
            <div data-testid="quiz-form-error" className="text-sm text-[#991B1B] bg-[#FEF2F2] border-l-4 border-[#991B1B] p-3">
              {formError}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setStep(STEPS.INTRO)} className="btn-secondary">Back</button>
            <button
              type="submit"
              data-testid="quiz-form-begin"
              disabled={loadingQs}
              className={`btn-primary ${loadingQs ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              {loadingQs ? "Preparing…" : "Begin Quiz"} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </form>
      </div>
    );
  }

  // ---------- RESULT ----------
  if (step === STEPS.RESULT && result) {
    const pct = Math.round((result.score / result.total) * 100);
    const eligible = result.certificate_eligible;
    return (
      <div data-testid="quiz-result" className="fade-in max-w-4xl mx-auto px-6 lg:px-12 py-16">
        <div className="card p-10 lg:p-14 text-center">
          <div className="text-6xl mb-4">{result.badge.emoji}</div>
          <div className="overline mb-2">{t.quiz.score}</div>
          <div className="font-display text-6xl font-black tracking-tight text-[#002FA7]">
            {result.score}<span className="text-3xl text-[#475569] font-bold">/{result.total}</span>
          </div>
          <h2 className="font-display text-3xl font-bold tracking-tight mt-6 text-[#0F172A]">
            {result.badge.title}
          </h2>
          <p className="mt-3 text-[#475569] max-w-md mx-auto leading-relaxed">{result.badge.message}</p>
          <div className="mt-4 inline-flex items-center gap-2 text-sm text-[#475569]">
            <div className="w-32 h-1.5 bg-[#E2E8F0] rounded-full overflow-hidden">
              <div className="h-full bg-[#002FA7]" style={{ width: `${pct}%` }} />
            </div>
            <span>{pct}%</span>
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <button data-testid="retake-quiz" onClick={restart} className="btn-secondary">
              <RotateCcw className="w-4 h-4" strokeWidth={1.5} /> {t.quiz.retake}
            </button>
            {eligible && (
              <button data-testid="download-certificate" onClick={printCertificate} className="btn-primary">
                <Download className="w-4 h-4" strokeWidth={1.5} /> Download Certificate
              </button>
            )}
          </div>

          {!eligible && (
            <p className="mt-6 text-xs text-[#94A3B8] max-w-md mx-auto">
              Score 9 or higher (out of 10) to earn the Certified Civic-Aware Traveller certificate.
            </p>
          )}
        </div>

        {/* Certificate (hidden, used for printing) */}
        {eligible && result.certificate && (
          <div className="mt-10" data-testid="certificate-preview">
            <h3 className="font-display text-2xl font-bold tracking-tight mb-4 text-[#0F172A]">
              <Award className="w-6 h-6 inline mr-2 text-[#002FA7]" strokeWidth={1.5} />
              Your Certificate
            </h3>
            <div ref={certRef} className="cert bg-white border-4 border-[#002FA7] p-10 lg:p-16 relative" style={{ fontFamily: "Georgia, serif" }}>
              <h1 className="text-3xl lg:text-4xl text-center text-[#002FA7] tracking-widest font-bold mb-1">
                CERTIFICATE
              </h1>
              <div className="sub text-center text-[#475569] uppercase tracking-[0.4em] text-xs mb-8">
                OF CIVIC AWARENESS
              </div>
              <p className="text-center text-[#475569] text-sm">This is to certify that</p>
              <div className="name text-3xl lg:text-4xl text-center text-[#0F172A] font-bold mt-3 mb-2">
                {result.certificate.name}
              </div>
              <div className="line mx-auto w-2/3 h-px bg-[#0F172A] mb-4" />
              <p className="body text-center text-[#0F172A] text-base leading-relaxed max-w-xl mx-auto">
                has demonstrated outstanding awareness of civic etiquette, cultural sensitivity and travel
                conduct relevant to a journey to <strong>{result.certificate.country}</strong>, scoring
                {" "}<strong>{result.certificate.score}/{result.certificate.total}</strong> on the Civic
                Sense Abroad assessment.
              </p>
              <div className="meta flex justify-between mt-12 text-xs text-[#475569] tracking-wide">
                <div>
                  <div className="font-bold text-[#0F172A]">Issued</div>
                  {result.certificate.issued_on}
                </div>
                <div className="text-center">
                  <div className="font-bold text-[#0F172A]">Passport No.</div>
                  {result.certificate.passport || "—"}
                </div>
                <div className="text-right">
                  <div className="font-bold text-[#0F172A]">Certificate ID</div>
                  {result.certificate.certificate_id}
                </div>
              </div>
              {result.certificate.email && (
                <div className="text-center text-[10px] text-[#94A3B8] tracking-widest uppercase mt-6">
                  Issued to · {result.certificate.email}
                </div>
              )}
              <div className="seal absolute right-10 bottom-10 w-24 h-24 lg:w-28 lg:h-28 rounded-full border-2 border-[#002FA7] flex items-center justify-center text-[#002FA7] font-bold text-[10px] text-center leading-tight">
                CIVIC<br />SENSE<br />ABROAD
              </div>
            </div>
          </div>
        )}

        <h3 className="font-display text-2xl font-bold tracking-tight mt-12 mb-6 text-[#0F172A]">
          Review
        </h3>
        <div className="space-y-4">
          {result.breakdown.map((b, i) => (
            <div key={b.question_id} data-testid={`review-item-${i}`} className="card p-6">
              <div className="flex items-start gap-3 mb-3">
                <div className={`shrink-0 w-6 h-6 rounded-sm flex items-center justify-center ${b.is_correct ? "pill-do" : "pill-dont"}`}>
                  {b.is_correct ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                </div>
                <p className="font-medium text-[#0F172A]">{b.question}</p>
              </div>
              <div className="pl-9 text-sm">
                {!b.is_correct && (
                  <p className="text-[#991B1B] mb-1">
                    Your answer: {b.options[b.selected]}
                  </p>
                )}
                <p className="text-[#065F46]">Correct: {b.options[b.correct]}</p>
                <p className="text-[#475569] mt-2 italic">{t.quiz.explanation}: {b.explanation}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ---------- QUIZ ----------
  const q = questions[idx];
  if (!q) return <div className="p-10 text-[#475569]">Loading…</div>;
  const selected = answers[q.id];
  const canProceed = selected !== undefined;
  const isLast = idx === questions.length - 1;

  return (
    <div data-testid="quiz-question-page" className="fade-in max-w-3xl mx-auto px-6 py-16">
      <div className="flex items-center justify-between mb-8">
        <div className="overline">Question {idx + 1} of {questions.length}</div>
        <div className="text-xs text-[#475569] pill">
          {q.category === "country" ? q.country : "Universal"}
        </div>
      </div>
      <div className="w-full h-1 bg-[#E2E8F0] mb-10 rounded-full overflow-hidden">
        <div className="h-full bg-[#002FA7] transition-all duration-300" style={{ width: `${((idx + 1) / questions.length) * 100}%` }} />
      </div>

      <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#0F172A] leading-snug">
        {q.question}
      </h2>

      <div className="mt-8 space-y-3">
        {q.options.map((opt, i) => {
          const isSelected = selected === i;
          return (
            <button
              key={i}
              data-testid={`quiz-option-${i}`}
              onClick={() => select(q.id, i)}
              className={`w-full text-left p-5 rounded-sm border transition-all ${
                isSelected
                  ? "border-[#002FA7] bg-[#EEF2FF]"
                  : "border-[#E2E8F0] bg-white hover:border-[#94A3B8]"
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-bold ${
                  isSelected ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-[#94A3B8] text-[#94A3B8]"
                }`}>
                  {String.fromCharCode(65 + i)}
                </div>
                <span className="text-[#0F172A]">{opt}</span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-10 flex justify-end">
        <button
          data-testid="quiz-next-button"
          onClick={next}
          disabled={!canProceed || submitting}
          className={`btn-primary ${!canProceed || submitting ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          {submitting ? "Submitting…" : isLast ? t.quiz.submit : t.quiz.next}
          {!submitting && <ArrowRight className="w-4 h-4" strokeWidth={1.5} />}
        </button>
      </div>
    </div>
  );
}
